import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../utils/seo';

// ===== 会員募集ページ 全面刷新（新デザイン憲章適用・型正典= home/page.tsx VII + about/overview） =====
// 事実正典（創作禁止・実文出典）:
//   [M1] src/pages/home/page.tsx VII（会員3種比較・年会費・特典＝データ一致必須）
//   [M2] 旧 src/pages/membership/page.tsx（working tree 実文：特典/入会の流れ/支払/FAQ/注意事項）
//   [M3] mng-thebutler/00_事業説明.md L19-27,L61-64（少数精鋭・専門性の裏付け）
// 年会費（¥30,000/¥60,000/¥150,000）はトップVIIと同一。実データ変更禁止。

// 紺帯番号バンド（home の NavyBand と同一文法：紋章+ローマ番号+和文左詰め+英字右詰め。
// 帯＝区切り＝帯境界に別の区切り線を入れない[#31]／帯直後に同一文言見出しを反復しない[#33]）。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

export default function MembershipPage() {
  useEffect(() => {
    updateSEO(seoData['/membership']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '会員募集' }
  ];

  // BTN-08（home と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // I. 会員になる価値（旧本文[M2]を保持したエディトリアル行。画像は各ポイントに配置＝5点基準#18/#55）
  const values = [
    {
      en: 'Professional Growth', ja: 'プロフェッショナルとしての成長',
      img: '/images/generated/jba-fs-040-membership.jpg',
      alt: '執事の指導のもとで実務に即した技術を学ぶ会員の研修風景',
      body: '継続的な教育機会と資格取得支援により、執事としてのスキルを体系的に高めていただけます。業界の第一線に立つ理事から直接学べる貴重な機会を用意し、実務に即した知識と技術の習得を後押しします。セミナーでは理論だけでなく、現場で培われた経験に基づく実践的な内容にも触れていただけます。',
      tags: ['継続的な教育機会', '資格取得支援', '理事による直接指導', '実践的な学びの場'],
    },
    {
      en: 'Industry Network', ja: '業界ネットワークの構築',
      img: '/images/generated/jba-fs-041-membership-v6.jpg',
      alt: '会員同士が交流し情報交換を行う懇親の場',
      body: '全国の会員との交流と情報交換を通じて、貴重な人脈を築いていただけます。同じ目標を持つプロフェッショナルとの出会いは、新たなキャリアの機会や、実務での課題解決へとつながります。オンライン勉強会や交流イベントを通じて、地域を超えたつながりを育んでいただけます。',
      tags: ['全国の会員との交流', 'キャリア機会の創出', 'オンライン勉強会', '地域を超えたつながり'],
    },
    {
      en: 'Cultural Contribution', ja: '執事文化への貢献',
      img: '/images/generated/jba-fs-042-membership-v3.jpg',
      alt: '歴史ある調度に囲まれ執事文化の継承を語り合う様子',
      body: '協会活動を通じて、日本における執事文化の継承と発展に直接貢献していただけます。130年以上の伝統を次世代へ受け継ぐ大切な役割を担い、業界全体の質の向上と社会的認知の広がりに参画いただけます。会員お一人おひとりの活動が、執事という職業の価値を高めることにつながります。',
      tags: ['130年の伝統継承', '業界の質の向上', '社会的認知の拡大', '職業価値の向上'],
    },
  ];

  // II. 会員区分（トップVIIと同一データ[M1]）
  const planRows = [
    { key: '会員証', j: 'デジタル会員証', s: '公式会員証（カード型）', c: '登録社員5名まで正会員特典' },
    { key: 'セミナー割引', j: '10%割引', s: '20%割引', c: '法人向け特別セミナー案内' },
    { key: '資格試験割引', j: '受験料10%割引', s: '受験料15%割引', c: '企業向けカスタム研修の相談' },
    { key: 'その他特典', j: '月次メールマガジン／会員専用コンテンツ', s: '特別セミナー優先予約／オンライン勉強会／総会での議決権', c: '採用情報の優先掲載' },
  ];

  // 会員区分の詳細（旧特典リストを全量保持[M2]）
  const plans = [
    {
      name: '準会員', fee: '¥30,000', target: '執事を目指す方・執事文化に関心のある方',
      featured: false,
      benefits: ['デジタル会員証発行', '月次メールマガジン配信', 'セミナー参加費10%割引', '資格試験受験料10%割引', 'メール質問対応（月1回）', '会員専用コンテンツへのアクセス'],
    },
    {
      name: '正会員', fee: '¥60,000', target: '執事資格をお持ちの方・実務経験のある方',
      featured: true,
      benefits: ['公式会員証発行（カード型）', '月次メールマガジン配信', 'セミナー参加費20%割引', '資格試験受験料15%割引', 'メール質問対応（月2回・優先対応）', '特別セミナー優先予約', '総会での議決権', 'オンライン勉強会への参加権'],
    },
    {
      name: '法人会員', fee: '¥150,000', target: 'ホテル・レストラン等の企業・団体',
      featured: false,
      benefits: ['登録社員5名まで正会員特典を適用', '企業向けカスタム研修のご相談', '採用情報の優先掲載', '法人向け特別セミナーのご案内', '追加登録（6名目以降：12,000円／名・年）'],
    },
  ];

  // III. 入会の流れ（旧[M2]保持）
  const steps = [
    { no: '01', title: 'オンラインでお申し込み', body: '申込フォームから必要事項をご入力ください。5分ほどで完了します。スマートフォンからでも簡単にお申し込みいただけます。' },
    { no: '02', title: '内容の確認', body: 'お申し込み内容を確認し、1週間以内にメールで結果をご連絡いたします。正会員の場合は、資格証明書や実務経験の確認を行います。' },
    { no: '03', title: '会員としての活動を開始', body: '承認後、会員証を発行し、すべての特典をご利用いただけます。初年度の会費をお支払いいただき、会員活動を始めていただけます。' },
  ];

  // IV. こんな方に選ばれています（旧[M2]保持）
  const audiences = [
    '執事資格の取得を目指している方',
    'すでに執事として活動されている方',
    'ホテル・レストランで接客に携わる方',
    '富裕層向けサービス業への転身をお考えの方',
    'ホスピタリティのスキルを高めたい方',
    '執事文化や国際マナーに関心のある方',
    '同じ志を持つ仲間とつながりたい方',
    '業界の最新情報を把握しておきたい方',
  ];

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img
          src="/images/generated/jba-p-014-mem-toast.jpg"
          alt="会員が集う懇親の場。執事文化の発展を語り合う協会会員の様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Membership</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.42] tracking-[0.04em] mb-6">
              <span className="block">執事文化の未来を、</span>
              <span className="block">一緒に築きませんか</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              1890年（明治23年）の創設以来、全日本執事協会は日本における執事文化の継承と発展を使命としてきました。会員になることは、資格取得の手段にとどまらず、執事という職業の専門性を高め、業界の発展に貢献する活動への参画を意味します。準会員・正会員・法人会員の三つの区分から、目的に合わせてお選びいただけます。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== I. 会員になる価値 — エディトリアル画像行（各ポイントに画像・左右交互）===== */}
      <NavyBand no="I" ja="会員になる価値" en="Why Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-14 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              入会で得られる、<span className="text-[#a38e65]">三つの価値</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              少数精鋭の会員制度により、お一人おひとりに質の高いサービスと学習の機会をお届けします。プロフェッショナルとしての成長、同じ志を持つ仲間とのネットワーク、そして執事文化の継承への貢献。会員として過ごす時間は、キャリアを次のステージへと導く確かな環境になります。
            </p>
          </div>

          <div className="space-y-10 sm:space-y-14">
            {values.map((v, i) => (
              <div key={v.en} className="grid lg:grid-cols-2 items-center gap-8 lg:gap-14 border border-[#e2ddd2] overflow-hidden">
                <div className={`relative min-h-[260px] lg:min-h-[360px] bg-black ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img src={v.img} alt={v.alt} className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
                <div className={`px-7 sm:px-10 lg:px-12 py-10 lg:py-12 text-left ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">{v.en}</span>
                  <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[26px] leading-[1.5] tracking-[0.02em] mb-4">{v.ja}</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-[#333] text-sm sm:text-base leading-[1.95] mb-6">{v.body}</p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {v.tags.map((t) => (
                      <li key={t} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[6px]">◆</span><span>{t}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== II. 会員区分と年会費 — リード+画像＋細罫テーブル調（トップVIIとデータ一致）===== */}
      <NavyBand no="II" ja="会員区分と年会費" en="Plans & Fees" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 sm:mb-16">
            <div className="order-2 lg:order-1 text-left">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                目的に合わせて選べる、<span className="text-[#a38e65]">三つの会員区分</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95] mb-4">
                入会金はいずれの区分も無料です。準会員・正会員・法人会員の三区分をご用意し、資格取得支援、セミナー優待、ネットワーキングの機会など、区分に応じた特典をお選びいただけます。
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
                会費は、執事という職業の専門性と社会的な意義を反映した設定としています。富裕層やVIPに高度なサービスを提供するプロフェッショナルにふさわしい協会運営と会員サービスを、この会費で支えています。
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/generated/jba-space-lounge-01.jpg" alt="紫のカーテンのサロンラウンジ" className="w-full h-[300px] sm:h-[400px] md:h-[440px] object-cover border border-[#a38e65]/30" />
            </div>
          </div>

          {/* 細罫テーブル調（トップVIIと同一データ・年会費は真鍮金26px級serif強調） */}
          <div className="border-t-2 border-[#1a2942] overflow-x-auto">
            <table className="w-full border-collapse min-w-[680px]">
              <thead>
                <tr className="border-b border-[#e2ddd2]">
                  <th className="text-left py-5 pr-4 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] font-medium align-bottom w-[26%]">会員区分</th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">準会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥30,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom bg-[#f8f6f0]">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">正会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥60,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">法人会員</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥150,000<span className="text-xs text-[#8f7a56] ml-1">/年</span></div>
                  </th>
                </tr>
              </thead>
              <tbody className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]">
                {planRows.map((r) => (
                  <tr key={r.key} className="border-b border-[#e2ddd2]">
                    <td className="py-4 pr-4 text-[#1a2942] font-medium">{r.key}</td>
                    <td className="py-4 px-4">{r.j}</td>
                    <td className="py-4 px-4 bg-[#f8f6f0]">{r.s}</td>
                    <td className="py-4 px-4">{r.c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 会員区分の詳細（特典を全量掲載・正会員を強調） */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10 mt-12 sm:mt-14">
            {plans.map((p) => (
              <div key={p.name} className={`flex flex-col text-left border ${p.featured ? 'bg-[#1a2942] border-[#a38e65]' : 'bg-white border-[#e2ddd2]'}`}>
                <div className={`px-7 sm:px-8 py-7 border-b ${p.featured ? 'border-[#a38e65]/40' : 'border-[#e2ddd2]'}`}>
                  {p.featured && <span className="inline-block font-['Playfair_Display',serif] italic text-[#d7b779] text-xs tracking-[0.14em] mb-2">Most Popular</span>}
                  <h4 className={`jba-display font-medium text-xl sm:text-2xl mb-2 ${p.featured ? 'text-[#f8f6f0]' : 'text-[#1a2942]'}`}>{p.name}</h4>
                  <div className={`font-['Playfair_Display',serif] text-3xl sm:text-[32px] leading-none ${p.featured ? 'text-[#d7b779]' : 'text-[#a38e65]'}`}>{p.fee}<span className={`text-sm ml-1 ${p.featured ? 'text-[#9d9686]' : 'text-[#8f7a56]'}`}>/年</span></div>
                  <div className={`font-['Noto_Serif_JP',serif] text-xs mt-1 ${p.featured ? 'text-[#9d9686]' : 'text-[#8f7a56]'}`}>入会金：無料</div>
                </div>
                <div className="px-7 sm:px-8 py-7 flex flex-col flex-1">
                  <p className={`font-['Noto_Serif_JP',serif] text-sm mb-5 leading-[1.85] ${p.featured ? 'text-[#e7d9b6]' : 'text-[#8f7a56]'}`}>対象：{p.target}</p>
                  <ul className="space-y-0">
                    {p.benefits.map((b) => (
                      <li key={b} className={`flex items-start gap-3 py-2.5 border-b font-['Noto_Serif_JP',serif] text-sm ${p.featured ? 'border-[#a38e65]/25 text-[#ded8cc]' : 'border-[#e2ddd2] text-[#4a4638]'}`}><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{b}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== III. 入会はかんたん三段階 — 番号付き縦ステップ（新バリエーション①）
           由来注記: about/overview の番号付き2カラムブロックの文法を、手続き三段階の縦積みステップへ翻案。
           トップに手続きステップ型は無いため新造。===== */}
      <NavyBand no="III" ja="入会はかんたん三段階" en="How to Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-16 items-start">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-8">
                お申し込みから会員登録まで、<span className="text-[#a38e65]">最短5分</span>
              </h3>
              <div className="border-t border-[#e2ddd2]">
                {steps.map((s) => (
                  <div key={s.no} className="flex gap-5 sm:gap-7 border-b border-[#e2ddd2] py-7">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none shrink-0 pt-1">{s.no}</span>
                    <div>
                      <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.5]">{s.title}</h4>
                      <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pt-2">
              <img src="/images/generated/jba-p-015-mem-consult.jpg" alt="入会について案内する協会スタッフと相談に訪れた方の様子" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 mb-5" />
              <a href="/contact" className={`${BTN08_ON_LIGHT} w-full`}>入会について相談する</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IV. こんな方に選ばれています — 読者価値の該当者グリッド ===== */}
      <NavyBand no="IV" ja="こんな方に選ばれています" en="Who It's For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-8">
                学びと実務の、<span className="text-[#a38e65]">どちらの入口にも</span>
              </h3>
              <ul className="border-t border-[#e2ddd2]">
                {audiences.map((a) => (
                  <li key={a} className="flex items-start gap-3.5 border-b border-[#e2ddd2] py-3.5 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.75]"><span className="text-[#a38e65] mt-[3px]"><i className="ri-check-line"></i></span><span>{a}</span></li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/generated/jba-fs-045-membership-v6.jpg" alt="協会に集う会員の一人。落ち着いた品格をたたえる執事の姿" className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover object-top border border-[#a38e65]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== V. お支払いと年会費の更新 — 定義的2カラム＋更新の注記 ===== */}
      <NavyBand no="V" ja="お支払いと年会費の更新" en="Payment & Renewal" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              安心してお続けいただける、<span className="text-[#a38e65]">三つのお支払い方法</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              年会費のお支払いには、クレジットカード・銀行振込・口座振替の三つの方法をご用意しています。ご都合に合わせてお選びいただけます。
            </p>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
            <dl className="border-t-2 border-[#1a2942]">
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">クレジットカード</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">VISA／MasterCard／JCB／American Express をご利用いただけます。</dd>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">銀行振込</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">指定口座へのお振込みです。振込手数料はお客様のご負担となります。</dd>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">口座振替</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">2年目以降は自動引き落としの設定が可能です。</dd>
              </div>
            </dl>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8">
              <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Renewal</span>
              <h4 className="jba-display font-medium text-[#f8f6f0] text-lg mb-3">年会費の更新</h4>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">更新は毎年4月です。初年度は入会月から起算し、以降は毎年4月に年度更新となります。年度途中でのご入会は、初年度分を月割で計算いたします。</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VI. ご入会前にご確認ください — 注意事項＋運営方針（旧[M2]保持・⚠️絵文字は排し細罫箇条書きへ）===== */}
      <NavyBand no="VI" ja="ご入会前にご確認ください" en="Before You Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="border border-[#e2ddd2] p-7 sm:p-9 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-2">Membership</span>
              <h4 className="jba-display font-medium text-[#1a2942] text-xl mb-4">会員制度のご留意点</h4>
              <ul className="space-y-0">
                {['会員特典は段階的に拡充してまいります。', '一部の特典は準備が整い次第、順次提供を開始いたします。', '特典の内容は予告なく変更される場合がございます。'].map((t) => (
                  <li key={t} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="border border-[#e2ddd2] p-7 sm:p-9 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-2">Qualification</span>
              <h4 className="jba-display font-medium text-[#1a2942] text-xl mb-4">会員資格のご留意点</h4>
              <ul className="space-y-0">
                {['会員になっても就職や収入を保証するものではありません。', '資格の取得は、ご本人の努力と学習によります。', '年度途中での退会でも、会費の返金はいたしかねます。'].map((t) => (
                  <li key={t} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-[#f8f6f0] border border-[#a38e65]/30 p-7 sm:p-9 mt-8 text-left">
            <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-3">少数精鋭という運営方針</h4>
            <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.95]">
              当協会は少数精鋭の運営体制により、お一人おひとりの会員に質の高いサービスを提供することを大切にしています。大規模な協会では実現の難しい、きめ細やかな個別対応と丁寧なサポートをお届けいたします。
            </p>
          </div>
        </div>
      </section>

      {/* FAQ は Layout が /membership 用に自動注入する（home/about と同じ機構。pagesWithOwnFaq 非登録＝二重掲載回避のため本文にはインライン化しない）。 */}

      {/* ===== 締めCTAパネル（home 締めと同型）===== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 md:p-14 text-center">
            <h3 className="jba-display font-medium text-2xl sm:text-3xl text-[#f8f6f0] tracking-[0.04em]"><span className="inline-block">今すぐ会員登録して、</span><span className="inline-block">執事文化の一員になりませんか</span></h3>
            <div className="w-11 h-px bg-[#a38e65] mx-auto mt-4 mb-6"></div>
            <p className="text-base sm:text-lg text-[#e7e2d8] mb-8 font-['Noto_Serif_JP',serif] max-w-2xl mx-auto leading-[1.9]"><span className="inline-block">お申し込みはオンラインフォームから</span><span className="inline-block">簡単にお手続きいただけます。</span><span className="inline-block">ご不明な点がございましたら、</span><span className="inline-block">お気軽にお問い合わせください。</span></p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <a href="/contact" className={BTN08_ON_NAVY}>入会について相談する</a>
              <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
